import * as React from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/**
 * Searchable batch combobox for delivery-note / production lines.
 *
 * Sorts the passed-in batches by FEFO (earliest MHD first). Users can
 * search by charge number or MHD.
 *
 * Props:
 *   batches:    [{id, charge_number, mhd, quantity}]
 *   value:      selected batch id, or "" for none
 *   onChange:   (batchId) => void  (empty string clears the selection)
 *   testId:     optional prefix for data-testid attributes
 *   disabled:   greys out the trigger
 *   onCreate:   optional callback; if set, footer "➕ Neue Charge anlegen"
 *               is rendered.
 */
export default function BatchCombobox({
  batches = [], value = "", onChange, testId, disabled = false,
  onCreate,
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  // FEFO: sort ascending by MHD (empty MHD last).
  const sorted = React.useMemo(() => {
    return [...batches].sort((a, b) => {
      if (!a.mhd && !b.mhd) return 0;
      if (!a.mhd) return 1;
      if (!b.mhd) return -1;
      return a.mhd < b.mhd ? -1 : a.mhd > b.mhd ? 1 : 0;
    });
  }, [batches]);
  const selected = sorted.find((b) => b.id === value);
  const triggerCreate = () => {
    if (!onCreate) return;
    onCreate(search.trim());
    setOpen(false);
    setSearch("");
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          data-testid={testId ? `${testId}-trigger` : undefined}
          className={cn(
            "w-full justify-between h-10 font-normal",
            !selected && "text-[var(--text-muted)]",
          )}
        >
          <span className="truncate flex items-baseline gap-2 min-w-0">
            {selected ? (
              <>
                <span className="font-mono text-[11px] text-[var(--text-muted)] shrink-0">{selected.charge_number}</span>
                <span className="truncate">MHD {selected.mhd || "—"}</span>
              </>
            ) : "Charge…"}
          </span>
          <ChevronsUpDown size={14} className="ml-2 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[min(360px,90vw)]" align="start">
        <Command
          filter={(itemValue, s) => {
            if (!s) return 1;
            const q = s.trim().toLowerCase();
            return itemValue.toLowerCase().includes(q) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Suchen (Charge oder MHD)…"
                        value={search}
                        onValueChange={setSearch}
                        data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList className="max-h-72">
            <CommandEmpty>
              <div className="py-4 text-sm text-[var(--text-muted)]">Keine Charge.</div>
            </CommandEmpty>
            <CommandGroup>
              <CommandItem value="none" onSelect={() => { onChange(""); setOpen(false); setSearch(""); }}
                            className="cursor-pointer text-[var(--text-muted)]"
                            data-testid={testId ? `${testId}-option-none` : undefined}>
                <Check size={14} className={cn("mr-2", value === "" ? "opacity-100" : "opacity-0")} />
                Ohne Charge
              </CommandItem>
              {sorted.map((b) => {
                const key = `${b.charge_number}##${b.mhd || ""}##${b.id}`;
                return (
                  <CommandItem
                    key={b.id}
                    value={key}
                    data-testid={testId ? `${testId}-option-${b.id}` : undefined}
                    onSelect={() => { onChange(b.id); setOpen(false); setSearch(""); }}
                    className="cursor-pointer"
                  >
                    <Check size={14} className={cn("mr-2", value === b.id ? "opacity-100" : "opacity-0")} />
                    <span className="font-mono text-[11px] text-[var(--text-muted)] mr-2">{b.charge_number}</span>
                    <span className="truncate">MHD {b.mhd || "—"}</span>
                    {b.quantity != null && <span className="ml-auto text-[10px] text-[var(--text-muted)]">{b.quantity}</span>}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          {onCreate && (
            <div className="border-t border-[var(--border)] p-1">
              <button type="button"
                      onClick={triggerCreate}
                      data-testid={testId ? `${testId}-create` : undefined}
                      className="w-full flex items-center gap-2 rounded-md px-2 py-2 text-sm text-left hover:bg-[var(--pop-cream)]/60 text-[var(--pop-pink)] font-semibold">
                <Plus size={14} />
                <span className="truncate">Neue Charge anlegen</span>
              </button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
